import { useEffect, useRef } from 'react';
import type { ITelemetryItem } from '@microsoft/applicationinsights-web';
import { getApplicationInsights } from '@/lib/application-insights';
import type { InspectorEntry } from '../inspector/protocol';
import { buildMachineEventTelemetry } from '../inspector/telemetry/machineEventTelemetry';
import { extractIceServerUrls } from '../utils';

/** Subsystem lane an event came from; mirrors the inspector's origin badges. */
export type TelemetryOrigin = 'User' | 'Browser' | 'Backend' | 'Avatar' | 'Timer' | 'Machine';

/** `crypto.randomUUID` is secure-context only, so fall back to random bytes. */
const createVoiceLiveSessionId = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  const bytes = new Uint32Array(4);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(16).padStart(8, '0')).join('');
};

export interface UseVoiceLiveTelemetryOptions {
  /** Scenario id, merged into every tracked event/exception. */
  scenarioId: string;
}

export interface UseVoiceLiveTelemetryResult {
  /** Correlation id for this mounted session, stamped on every telemetry item. */
  voiceLiveSessionId: () => string;
  /** Record the backend-assigned FieldOne session id once the backend creates it. */
  setFieldOneSessionId: (sessionId: number) => void;
  /** Record the backend host (incl. port) for the active WebSocket. */
  setBackendHost: (host: string) => void;
  /** Current backend host, or '' before a connection attempt. */
  backendHost: () => string;
  /** Snapshot `performance.now()` as the start of a connect attempt. */
  markConnectStart: () => void;
  /** Milliseconds since the last {@link markConnectStart}, rounded. */
  elapsedMs: () => number;
  /** Extract + store STUN/TURN URLs (credentials excluded) from ICE servers. */
  setIceServerUrls: (servers: RTCIceServer[]) => void;
  /** Current ICE server URLs string, or '' before any are received. */
  iceServerUrls: () => string;
  /** Track a subsystem observation that has no machine-event counterpart. */
  trackDiagnostic: (
    origin: TelemetryOrigin,
    name: string,
    properties?: Record<string, unknown>,
  ) => void;
  /** Track one machine event as it happens, carrying its full inspector entry. */
  trackMachineEntry: (entry: InspectorEntry, sinceStartMs: number, replayed?: boolean) => void;
  /** Track an exception; `backendHost` + `scenarioId` are merged automatically. */
  trackException: (error: unknown, properties?: Record<string, unknown>) => void;
  /** Hand everything to the beacon; unload has no budget for the batched flush. */
  flushNow: () => void;
}

/**
 * Owns the App Insights instance and the stable telemetry context for a Voice
 * Live session (backend host, connect-start time, ICE server URLs). Exposes
 * `trackDiagnostic`/`trackMachineEntry`/`trackException` that auto-merge
 * `{ backendHost, scenarioId, voiceLiveSessionId }` so call sites only pass the
 * per-event fields. The context accessors return refs' current values, so they
 * are safe to read from async callbacks without stale-closure risk.
 *
 * Events are named `VoiceLive:<Origin>:<Name>`. SCREAMING_SNAKE names are real
 * machine events and match the inspector one-to-one; PascalCase names are
 * subsystem diagnostics with no machine counterpart.
 *
 * The initializer additionally stamps auto-collected items (fetch/XHR
 * dependencies, unhandled exceptions, page views) so one query returns the whole
 * session. Voice Live events carry the id in their own properties too, because
 * the initializer is scoped to the mount and teardown telemetry outlives it.
 */
export const useVoiceLiveTelemetry = ({
  scenarioId,
}: UseVoiceLiveTelemetryOptions): UseVoiceLiveTelemetryResult => {
  const appInsightsRef = useRef(getApplicationInsights());
  const backendHostRef = useRef<string>(''); // url.host of the WebSocket backend (includes port)
  const connectStartTimeRef = useRef<number>(0); // performance.now() at the start of each connect attempt
  const iceServerUrlsRef = useRef<string>(''); // extracted STUN/TURN URLs (credentials excluded)
  const voiceLiveSessionIdRef = useRef<string>('');
  const fieldOneSessionIdRef = useRef<number | null>(null); // backend-assigned, arrives mid-session

  if (!voiceLiveSessionIdRef.current) {
    voiceLiveSessionIdRef.current = createVoiceLiveSessionId();
  }

  // Scoped to the mounted session: the App Insights client is a module singleton,
  // so the handler is removed on unmount to stop stamping unrelated telemetry.
  useEffect(() => {
    const handler = appInsightsRef.current?.addTelemetryInitializer((item: ITelemetryItem) => {
      item.data = item.data ?? {};
      item.data.voiceLiveSessionId = voiceLiveSessionIdRef.current;
      item.data.appVersion = process.env.NEXT_PUBLIC_APP_VERSION ?? '';
      if (fieldOneSessionIdRef.current !== null) {
        // Stringified so it lands in customDimensions; numbers are split into customMeasurements.
        item.data.fieldOneSessionId = String(fieldOneSessionIdRef.current);
      }
    });

    return () => handler?.remove();
  }, []);

  const voiceLiveSessionId = () => voiceLiveSessionIdRef.current;

  const setFieldOneSessionId = (sessionId: number) => {
    fieldOneSessionIdRef.current = sessionId;
  };

  const setBackendHost = (host: string) => {
    backendHostRef.current = host;
  };
  const backendHost = () => backendHostRef.current;

  const markConnectStart = () => {
    connectStartTimeRef.current = performance.now();
  };
  const elapsedMs = () => Math.round(performance.now() - connectStartTimeRef.current);

  const setIceServerUrls = (servers: RTCIceServer[]) => {
    iceServerUrlsRef.current = extractIceServerUrls(servers);
  };
  const iceServerUrls = () => iceServerUrlsRef.current;

  const track = (name: string, properties?: Record<string, unknown>) => {
    appInsightsRef.current?.trackEvent({
      name,
      properties: {
        backendHost: backendHostRef.current,
        scenarioId,
        voiceLiveSessionId: voiceLiveSessionIdRef.current,
        ...properties,
      },
    });
  };

  const trackDiagnostic = (
    origin: TelemetryOrigin,
    name: string,
    properties?: Record<string, unknown>,
  ) => {
    track(`VoiceLive:${origin}:${name}`, { origin, tier: 'diagnostic', ...properties });
  };

  const trackMachineEntry = (entry: InspectorEntry, sinceStartMs: number, replayed = false) => {
    const { name, properties } = buildMachineEventTelemetry(entry, sinceStartMs);
    track(name, { ...properties, elapsedMs: elapsedMs(), replayed });
  };

  const trackException = (error: unknown, properties?: Record<string, unknown>) => {
    appInsightsRef.current?.trackException({
      exception: error instanceof Error ? error : new Error(String(error)),
      properties: { backendHost: backendHostRef.current, scenarioId, ...properties },
    });
  };

  const flushNow = () => appInsightsRef.current?.onunloadFlush();

  return {
    voiceLiveSessionId,
    setFieldOneSessionId,
    setBackendHost,
    backendHost,
    markConnectStart,
    elapsedMs,
    setIceServerUrls,
    iceServerUrls,
    trackDiagnostic,
    trackMachineEntry,
    trackException,
    flushNow,
  };
};
